"use client";

import { YoutubeLogo } from "@phosphor-icons/react";
import { useQuery } from "convex/react";
import Image from "next/image";
import Link from "next/link";

import { api } from "../../../convex/_generated/api";

import { SummitHeroMosaic } from "@/components/home/summit-hero-mosaic";
import type { Locale } from "@/i18n/config";

type HeroTranslations = {
  title: string;
  divine: string;
  fire: string;
  watchLatest: string;
  schoolOfDestiny: string;
  submitTestimony: string;
  liveNow: string;
  watchLive: string;
};

type HeroCarouselProps = {
  locale?: Locale;
  translations?: HeroTranslations;
};

export const HeroCarousel = ({ locale = 'en', translations }: HeroCarouselProps) => {
  const liveStream = useQuery(api.siteSettings.getLiveStream);
  const isLive = Boolean(liveStream?.active && liveStream.videoId);

  // Default translations for backwards compatibility
  const t: HeroTranslations = translations || {
    title: "Find Your",
    divine: "Divine",
    fire: "Fire",
    watchLatest: "Watch Latest",
    schoolOfDestiny: "School of Destiny",
    submitTestimony: "Submit a Testimony",
    liveNow: "Live now",
    watchLive: "Watch live",
  };

  return (
    <section className="relative flex h-screen min-h-[680px] items-center overflow-hidden bg-black md:h-[110vh] md:min-h-[900px]">
      <div className="absolute inset-0 z-0">
        {isLive ? (
          // 16:9 scaled to cover the viewport: YouTube letterboxes any other
          // sizing, which would show black bars through the hero gradient.
          <div className="absolute inset-0 overflow-hidden">
            <iframe
              src={`https://www.youtube-nocookie.com/embed/${liveStream!.videoId}?autoplay=1&mute=1&controls=0&playsinline=1&rel=0&modestbranding=1`}
              title={liveStream!.title || "Live stream"}
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
              className="pointer-events-none absolute left-1/2 top-1/2 h-[56.25vw] min-h-full w-[177.78vh] min-w-full -translate-x-1/2 -translate-y-1/2 opacity-60 dark:opacity-40"
            />
          </div>
        ) : (
          <SummitHeroMosaic />
        )}
        <div className="absolute inset-0 bg-black/65 md:bg-transparent" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(0,0,0,0.86)_0%,rgba(0,0,0,0.58)_44%,rgba(0,0,0,0.15)_78%),linear-gradient(0deg,rgba(0,0,0,0.78)_0%,transparent_52%,rgba(0,0,0,0.38)_100%)]" />
      </div>

      <div className="relative z-10 min-w-0 w-full px-6 pt-24 text-left md:px-[8vw]">
        {isLive && (
          <div className="mb-8 flex items-center gap-3 animate-hero-fade">
            <span className="h-3 w-3 animate-pulse bg-brand-red" aria-hidden />
            <span className="font-display text-sm font-bold uppercase tracking-[0.3em] text-brand-red">
              {t.liveNow}
            </span>
            {liveStream?.title && (
              <span className="font-display text-sm font-bold uppercase tracking-[0.2em] text-white/60">
                {liveStream.title}
              </span>
            )}
          </div>
        )}
        {locale === "en" ? (
          <h1 className="mb-6 w-[min(86vw,950px)] animate-hero-fade [animation-delay:100ms] md:w-[min(58vw,850px)]">
            <span className="sr-only">{`${t.title} ${t.divine} ${t.fire}`}</span>
            <Image
              src="/images/hero-divine-fire.png"
              alt=""
              width={1694}
              height={929}
              priority
              sizes="(min-width: 768px) 58vw, 86vw"
              className="h-auto w-full"
            />
          </h1>
        ) : (
          <h1 className="mb-8 max-w-5xl font-serif text-5xl font-medium leading-[0.82] tracking-tighter text-white animate-hero-fade [animation-delay:100ms] sm:text-7xl md:text-[9rem]">
            {t.title} <br />
            <span className="italic font-light">
              <span className="text-brand-gold">{t.divine} </span>
              <span className="text-[#ff6b22]">{t.fire}</span>
            </span>
          </h1>
        )}
        <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
          {isLive ? (
            <a
              href={`https://www.youtube.com/watch?v=${liveStream!.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-brand-red text-white px-14 py-6 rounded-full font-display font-bold text-sm uppercase tracking-widest hover:scale-105 transition-all shadow-2xl shadow-brand-red/40 flex items-center gap-3"
            >
              <YoutubeLogo weight="fill" className="w-5 h-5" />
              {t.watchLive}
            </a>
          ) : (
            <Link
              href={`/${locale}/testimony`}
              className="flex min-h-14 w-full items-center justify-center rounded-full bg-brand-red px-6 py-4 text-center font-display text-sm font-bold uppercase tracking-widest text-white shadow-2xl shadow-brand-red/40 transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:w-auto sm:px-14 sm:py-6"
            >
              {t.submitTestimony}
            </Link>
          )}
          <Link
            href={`/${locale}/watch`}
            className="flex min-h-14 w-full items-center justify-center gap-3 rounded-full bg-white px-6 py-4 font-display text-sm font-bold uppercase tracking-widest text-black transition-transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:w-auto sm:px-12 sm:py-6"
          >
            <YoutubeLogo weight="fill" className="w-5 h-5" />
            {t.watchLatest}
          </Link>
        </div>
      </div>
    </section>
  );
};
